/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useRef } from "react"
import { IxRefFor } from "@cd2/tools"
import { Form2 } from "@cd2/forms"
import { Button, Grid, Icon, List, PopOut } from "@cd2/components"
import { countries } from "../../../constants/countries"
import { IRecord } from "../../../types/schema"
import { P } from "@cd2/text"
import { IPostcodeLookupReturnValue, PostcodeField } from "./PostcodeField"
import { Form2TextFieldToUpperCase } from "../../FormFields/Form2TextFieldToUpperCase"
import { icons } from "../../../constants/icons"
import { CountrySelect } from "../../SelectLists/CountrySelect"
import { Form2ValidatePostcodeLength } from "../../../utils/Form2ValidatePostcodeLength"
import Tippy from "@tippyjs/react"
import { Instance } from "tippy.js"
import { autoCompleteOff } from "../../../constants/autoComplete"

type IAddress = IRecord<
  "addresses",
  | "label"
  | "name"
  | "contact"
  | "postcode"
  | "line_1"
  | "line_2"
  | "line_3"
  | "line_4"
  | "city"
  | "county"
  | "country"
  | "email"
  | "phone"
  | "stored"
>

interface IProps1 {
  initialData?: Partial<IAddress>
  actions?: never
  includeDefaultCheckbox?: { shipping?: boolean; billing?: boolean }
  suggestedEmails?: string[]

  onSubmit(data: IAddress): void
}

interface IProps2<T extends string> {
  initialData?: Partial<IAddress>
  actions: { children: React.ReactNode; name: T; disabled?: boolean; info?: string }[]
  includeDefaultCheckbox?: { shipping?: boolean; billing?: boolean }
  suggestedEmails?: string[]

  onSubmit(data: IAddress, action: T): void
}

type IProps<T extends string> = (IProps1 | IProps2<T>) & {
  fieldMaxLength?: number
}

export type IAddressFormProps<T extends string> = IProps<T>

const countryOptions = countries.map(country => ({ value: country.name, label: country.name }))

export function CustomerAddressForm<T extends string>({
  onSubmit,
  initialData,
  actions,
  includeDefaultCheckbox,
  suggestedEmails,
  fieldMaxLength,
}: IProps<T>) {
  const formRef = useRef<IxRefFor<typeof Form2.Form>>(null)
  const tippyRef = useRef<Instance | null>(null)

  function handlePostcodeSelect(value: IPostcodeLookupReturnValue) {
    if (!formRef.current) {
      throw new Error("No address form")
    }

    formRef.current.setPartialData({
      city: value.city,
      country: value.country,
      county: value.county,
      line_1: value.line_1,
      line_2: value.line_2,
      line_3: value.line_3,
      line_4: value.line_4,
      name: value.name,
      postcode: value.postcode,
    })
  }

  function handleSubmitWithAction(action: T) {
    if (!formRef.current) {
      throw new Error("No address form")
    }

    formRef.current.setPartialData({ action })
    formRef.current.submit()
  }

  function handleSubmit(data: any) {
    const { action, ...otherData } = data

    onSubmit(otherData, action)
  }

  return (
    <Form2.Form xRef={formRef} onSubmit={handleSubmit} initialData={initialData}>
      <Grid gap={12} columns={[{ fr: 1 }, { fr: 1 }]}>
        <Grid.Area column={[1, 2]}>
          <Form2TextFieldToUpperCase field="label" />
        </Grid.Area>
        <Grid.Area column={[1, 2]}>
          <Form2.WithField
            field="postcode"
            render={field => (
              <PostcodeField
                onPostcodeSelect={handlePostcodeSelect}
                postcodeFieldError={field.getError()}
              />
            )}
            subscribe
          />
          <Form2ValidatePostcodeLength field="postcode" />
        </Grid.Area>
        <Form2TextFieldToUpperCase field="name" label="Postal Name" required />
        <Form2TextFieldToUpperCase field="contact" />
        <Form2TextFieldToUpperCase field="line_1" maxLength={fieldMaxLength} required />
        <Form2TextFieldToUpperCase field="line_2" maxLength={fieldMaxLength} />
        <Form2TextFieldToUpperCase field="line_3" maxLength={fieldMaxLength} />
        <Form2TextFieldToUpperCase field="line_4" maxLength={fieldMaxLength} />
        <Form2TextFieldToUpperCase field="city" maxLength={fieldMaxLength} />
        <Form2TextFieldToUpperCase field="county" maxLength={fieldMaxLength} />
        <CountrySelect />
        <div>
          <Form2.WithField
            field="email"
            render={field => (
              <Tippy
                onCreate={instance => (tippyRef.current = instance)}
                content={
                  suggestedEmails && suggestedEmails.length === 0 ? null : (
                    <div
                      css={css`
                        min-width: 200px;
                        background: white;
                        border: 1px solid #afafaf;
                        border-radius: 3px;
                        box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
                        max-height: 200px;
                        overflow-y: auto;

                        h4 {
                          padding: 6px 12px;
                          margin: 0;
                        }

                        > div {
                          padding: 8px 12px;
                          cursor: pointer;

                          :hover {
                            background: #efefef;
                          }
                        }
                      `}
                    >
                      <h4>Contact emails</h4>
                      {suggestedEmails &&
                        suggestedEmails.map(name => (
                          <div
                            key={name}
                            onClick={() => {
                              field.setValue(name)
                              tippyRef.current?.hide()
                            }}
                          >
                            {name}
                          </div>
                        ))}
                    </div>
                  )
                }
                trigger="click"
                placement="bottom-start"
                interactive
              >
                <div>
                  <Form2.Email
                    field="email"
                    placeholder="someone@example.com"
                    inputProps={{
                      onFocus: () => tippyRef.current?.show(),
                      onBlur: () => setTimeout(() => tippyRef.current?.hide(), 100),
                      autoComplete: autoCompleteOff,
                    }}
                  />
                </div>
              </Tippy>
            )}
          />
        </div>
        <Form2TextFieldToUpperCase field="phone" />
      </Grid>
      {includeDefaultCheckbox?.billing && (
        <React.Fragment>
          <Form2.Checkbox field="isDefault" label="Set as default billing address" />
        </React.Fragment>
      )}
      {includeDefaultCheckbox?.shipping && (
        <React.Fragment>
          <Form2.Checkbox field="isDefaultShipping" label="Set as default shipping address" />
        </React.Fragment>
      )}
      <div>
        {!actions && <Form2.Submit />}
        {actions && (
          <List horizontal>
            {actions.map(action => (
              <Button
                key={action.name}
                buttonProps={{
                  type: "submit",
                  renderRight: action.info
                    ? () => (
                        <PopOut
                          placement="left"
                          target={() => <Icon color="black" icon={icons.alert} />}
                          openOn="hover"
                          render={() => (
                            <div
                              css={css`
                                border: 1px solid #ddd;
                                box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
                                width: 200px;
                                padding: 16px;
                                background-color: white;
                              `}
                            >
                              <P color="primary">{action.info}</P>
                            </div>
                          )}
                        />
                      )
                    : undefined,
                }}
                onClick={e => {
                  // we don't want this to trigger the native form submit as we will manually call it during
                  // the handleSubmitWithAction
                  e.preventDefault()
                  handleSubmitWithAction(action.name)
                }}
                disabled={!!action.disabled}
              >
                {action.children}
              </Button>
            ))}
          </List>
        )}
      </div>
    </Form2.Form>
  )
}
